import { coreApi, identityApi } from '$api-clients';
import { mapToAgentProfile } from './mapToAgentProfile';
import type { AgentProfileFields } from './types';

export interface FetchAgentProfileOptions {
  /**
   * Team member preferences are authz-gated: core-service 404s when the
   * caller lacks shared access to the target user. Opt in only when the
   * target is the caller's own/primary agent (e.g. transaction owner);
   * otherwise the profile's preference-derived fields are null.
   */
  loadPreferences?: boolean;
}

/**
 * Assembles a team member's full agent profile from its identity/core
 * resources. Any source failure throws, except a 404 on the member-scoped
 * brokerage, which maps to null brokerage fields (a member without a
 * brokerage is a valid state, not a failure).
 */
export async function fetchAgentProfile(
  userId: string,
  teamId: string,
  { loadPreferences = false }: FetchAgentProfileOptions = {},
): Promise<AgentProfileFields> {
  const memberPath = { params: { path: { teamId, userId } } };
  const teamPath = { params: { path: { teamId } } };

  const [
    memberResult,
    teamResult,
    brokerageResult,
    mlsResult,
    preferencesResult,
  ] = await Promise.all([
    identityApi.GET('/teams/{teamId}/members/{userId}', memberPath),
    identityApi.GET('/teams/{teamId}', teamPath),
    identityApi.GET('/teams/{teamId}/members/{userId}/brokerage', memberPath),
    identityApi.GET('/teams/{teamId}/mls-memberships', teamPath),
    loadPreferences
      ? coreApi.GET('/user-settings/{id}/team-member-preferences/{teamId}', {
          params: { path: { id: userId, teamId } },
        })
      : null,
  ]);

  if (memberResult.error !== undefined) {
    throw new Error('Failed to fetch team member', {
      cause: memberResult.error,
    });
  }
  if (teamResult.error !== undefined) {
    throw new Error('Failed to fetch team', { cause: teamResult.error });
  }
  if (mlsResult.error !== undefined) {
    throw new Error('Failed to fetch MLS memberships', {
      cause: mlsResult.error,
    });
  }
  const preferencesError = preferencesResult?.error;
  if (preferencesError !== undefined) {
    throw new Error('Failed to fetch team member preferences', {
      cause: preferencesError,
    });
  }
  if (
    brokerageResult.error !== undefined &&
    brokerageResult.response.status !== 404
  ) {
    throw new Error('Failed to fetch brokerage', {
      cause: brokerageResult.error,
    });
  }

  return mapToAgentProfile({
    member: memberResult.data,
    team: teamResult.data,
    brokerage: brokerageResult.data ?? null,
    mlsMemberships: mlsResult.data.data,
    preferences: preferencesResult?.data ?? null,
  });
}
